import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
import RoleBasedGuard from 'src/auth/guard/role-based-guard';

import { RouteGuard } from 'src/components/permissions';
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

const ListRoomPage = lazy(() => import('src/pages/medicore/rooms/list'));
const InventoryStatisticsPage = lazy(() => import('src/pages/medicore/inventory/statistics'));
const InventoryListViewPage = lazy(() => import('src/pages/medicore/inventory/list'));

const WarehouseViewPage = lazy(() => import('src/pages/medicore/inventory/warehouse'));
const MedicalOrderListPage = lazy(() => import('src/pages/medicore/inventory/listMedicalOrder'));
const PurchaseOrderListPage = lazy(() => import('src/pages/medicore/inventory/purchaseOrder-list'));
const PurchaseOrderNewPage = lazy(() => import('src/pages/medicore/inventory/purchaseOrder-new'));
const ProductsListPage = lazy(() => import('src/pages/medicore/inventory/list-product'));
const ProviderListPage = lazy(() => import('src/pages/medicore/inventory/list-provider'));
const CategoriesListPage = lazy(() => import('src/pages/medicore/inventory/list-category'));
const DispatchStatisticsPage = lazy(
  () => import('src/pages/medicore/inventory/dispatch-statistics')
);
const DispatchProductPage = lazy(() => import('src/pages/medicore/inventory/dispatch-product'));
const RequisitionRequestListPage = lazy(
  () => import('src/pages/medicore/inventory/requisitionRequest-list')
);
const ExpendableMaterialsListPage = lazy(
  () => import('src/pages/medicore/inventory/expendable-materials')
);

// ----------------------------------------------------------------------

/**
 * Componente wrapper que combina RouteGuard con Suspense y lazy loading
 *
 * CARACTERÍSTICAS:
 * - Mantiene la funcionalidad existente de lazy loading con Suspense
 * - Agrega protección por permisos específicos sin romper el flujo existente
 * - Muestra página de acceso denegado si no tiene permisos
 * - Redirige a fallbackRoute si showFallback es false
 * - Los usuarios master/admin tienen acceso automático a todo
 *
 * PATRÓN PARA REPLICAR:
 * 1. Envolver cada elemento de ruta con <ProtectedRoute route="/path/exacto">
 * 2. Mantener la estructura children existente
 * 3. Mapear la ruta en ROUTE_PERMISSIONS en src/utils/permissions.js
 * 4. Los permisos se verifican automáticamente contra el usuario logueado
 */
const ProtectedRoute = ({ route, children }) => (
  <RouteGuard route={route} fallbackRoute="/medicore" showFallback>
    <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
  </RouteGuard>
);

ProtectedRoute.propTypes = {
  route: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ----------------------------------------------------------------------

export const medicoreInventoryRoutes = [
  {
    path: 'medicore/inventory',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['doctor', 'employee']}>
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'statistics',
        element: (
          <ProtectedRoute route="/medicore/inventory/statistics">
            <InventoryStatisticsPage />
          </ProtectedRoute>
        ),
        index: true,
      },
      {
        path: 'list',
        element: (
          <ProtectedRoute route="/medicore/inventory/list">
            <InventoryListViewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'warehouse',
        element: (
          <ProtectedRoute route="/medicore/inventory/warehouse">
            <WarehouseViewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'listMedicalOrder',
        element: (
          <ProtectedRoute route="/medicore/inventory/listMedicalOrder">
            <MedicalOrderListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'listExpendableMaterials',
        element: (
          <ProtectedRoute route="/medicore/inventory/listExpendableMaterials">
            <ExpendableMaterialsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'purchaseOrder',
        children: [
          {
            path: 'listPurchaseOrder',
            element: (
              <ProtectedRoute route="/medicore/inventory/purchaseOrder/listPurchaseOrder">
                <PurchaseOrderListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'newPurchaseOrder',
            element: (
              <ProtectedRoute route="/medicore/inventory/purchaseOrder/newPurchaseOrder">
                <PurchaseOrderNewPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'requisition',
        children: [
          {
            path: 'listRequisition',
            element: (
              <ProtectedRoute route="/medicore/inventory/requisition/listRequisition">
                <RequisitionRequestListPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'listProduct',
        element: (
          <ProtectedRoute route="/medicore/inventory/listProduct">
            <ProductsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'listProvider',
        element: (
          <ProtectedRoute route="/medicore/inventory/listProvider">
            <ProviderListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'listCategory',
        element: (
          <ProtectedRoute route="/medicore/inventory/listCategory">
            <CategoriesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dispatch',
        children: [
          {
            path: 'statistics',
            element: (
              <ProtectedRoute route="/medicore/inventory/dispatch/statistics">
                <DispatchStatisticsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'dispatchProduct',
            element: (
              <ProtectedRoute route="/medicore/inventory/dispatch/dispatchProduct">
                <DispatchProductPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'dispatchWarehouse',
            element: (
              <ProtectedRoute route="/medicore/inventory/dispatch/dispatchWarehouse">
                <ListRoomPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
];
